import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, selectCls, Globe, Check, Link, ArrowRight } from './shared';

export default function CrossChainTab() {
  const [chains, setChains] = useState({});
  const [ccStats, setCcStats] = useState(null);
  const [attAddr, setAttAddr] = useState('');
  const [attChain, setAttChain] = useState('arbitrum');
  const [attLoading, setAttLoading] = useState(false);
  const [attResult, setAttResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/agent-credit/crosschain/chains`).then(r => r.json()).then(d => setChains(d.chains || {})).catch(() => {});
    fetch(`${API}/api/agent-credit/crosschain/stats`).then(r => r.json()).then(setCcStats).catch(() => {});
  }, []);

  const generate = async (e) => {
    e.preventDefault();
    setAttLoading(true); setAttResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/crosschain/attestation/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_address: attAddr.trim(), target_chain: attChain }),
      });
      const data = await res.json();
      setAttResult(res.ok ? { type: 'success', data } : { type: 'error', msg: data.detail });
    } catch { setAttResult({ type: 'error', msg: 'Network error' }); }
    finally { setAttLoading(false); }
  };

  const chainStatus = { live: 'bg-green-500', planned: 'bg-yellow-500' };

  return (
    <div data-testid="crosschain-tab">
      {ccStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Globe} label="Total Attestations" value={ccStats.total_attestations} color="blue" />
          <StatCard icon={Check} label="Valid" value={ccStats.valid_attestations} color="green" />
          <StatCard icon={Link} label="Supported Chains" value={ccStats.supported_chains} color="purple" />
          <StatCard icon={ArrowRight} label="Most Ported To" value={Object.entries(ccStats.attestations_by_chain || {}).sort((a,b) => b[1]-a[1])[0]?.[0] || 'N/A'} color="orange" />
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        {Object.entries(chains).map(([key, chain]) => (
          <div key={key} className={`rounded-xl border p-4 ${chain.status === 'live' ? 'border-green-500/20 bg-green-500/[0.03]' : 'border-white/10 bg-white/[0.02]'}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm capitalize">{key}</span>
              <span className={`w-2 h-2 rounded-full ${chainStatus[chain.status] || 'bg-gray-500'}`} />
            </div>
            <div className="text-xs text-gray-500">{chain.name}</div>
            <div className="text-xs font-mono text-gray-600 mt-1">Chain ID: {chain.chain_id}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-purple-500/20 bg-purple-500/[0.03] p-6 mb-8">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-purple-400" /> Generate Credit Attestation</h3>
        <p className="text-xs text-gray-500 mb-4">Create a portable proof of an agent's credit standing for use on another chain.</p>
        <form onSubmit={generate} className="grid md:grid-cols-3 gap-4">
          <Field label="Agent Address"><input value={attAddr} onChange={e => setAttAddr(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="cc-agent-address" /></Field>
          <Field label="Target Chain">
            <select value={attChain} onChange={e => setAttChain(e.target.value)} className={selectCls} data-testid="cc-target-chain">
              {Object.entries(chains).filter(([,c]) => c.status !== 'live').map(([key, chain]) => (
                <option key={key} value={key}>{chain.name}</option>
              ))}
            </select>
          </Field>
          <div className="flex items-end">
            <Button type="submit" disabled={attLoading} className="w-full h-12 bg-gradient-to-r from-purple-600 to-indigo-600" data-testid="cc-generate-btn">
              {attLoading ? 'Generating...' : 'Generate Attestation'}
            </Button>
          </div>
        </form>
        {attResult && (
          <div className={`mt-4 p-4 rounded-lg border text-sm ${attResult.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="cc-result">
            {attResult.type === 'success' ? (
              <div>
                <p className="flex items-center gap-2"><Check className="w-4 h-4" /> {attResult.data.message}</p>
                <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                  <div><span className="text-gray-500">ACS:</span> <span className="font-mono">{attResult.data.payload.acs_score}</span></div>
                  <div><span className="text-gray-500">Tier:</span> <span className="font-mono">{attResult.data.payload.tier}</span></div>
                  <div><span className="text-gray-500">Credit Limit:</span> <span className="font-mono">${attResult.data.payload.credit_limit?.toLocaleString()}</span></div>
                  <div><span className="text-gray-500">Valid Until:</span> <span className="font-mono">{attResult.data.payload.valid_until?.slice(0,10)}</span></div>
                </div>
                <p className="text-xs font-mono text-gray-600 mt-2 break-all">Hash: {attResult.data.attestation_hash}</p>
              </div>
            ) : (<p>{attResult.msg}</p>)}
          </div>
        )}
      </div>
    </div>
  );
}
